import { useMutation } from '@apollo/client';
import React, { FunctionComponent } from 'react';

import { EventTrack, pushEventTracking } from 'src/app/helpers/analytics';
import { withStyles } from 'src/app/helpers/styles';
import { getAddVideoToPlaylistOptions, getRemoveVideoFromPlaylistOptions } from 'src/app/helpers/playlist';
import { getAddVideoToWatchlistOptions, getRemoveVideoFromWatchlistOptions } from 'src/app/helpers/watchlist';
import { GRAPHQL_ADD_TO_PLAYLIST_MUTATION, GRAPHQL_REMOVE_FROM_PLAYLIST_MUTATION } from 'src/app/models/playlist';
import { InjectedToastProps } from 'src/app/models/toast';
import { GRAPHQL_ADD_WATCHLIST_MUTATION, GRAPHQL_REMOVE_WATCHLIST_MUTATION } from 'src/app/models/watchlist';

type MyInjectedProps = InjectedToastProps;

interface MyOwnProps {
  id: string;
  name: string;
  videoId: string;
  type: 'playlist' | 'watchlist';
  isChecked?: boolean;
  pushEventTrack?: EventTrack;
}

type MyProps = MyOwnProps & MyInjectedProps;

const PlaylistCheckboxComponent: FunctionComponent<MyProps> = (props: MyProps): JSX.Element | null => {
  const { id, name, isChecked, videoId, type, pushEventTrack } = props;
  const [addVideoToWatchlist] = useMutation(GRAPHQL_ADD_WATCHLIST_MUTATION);
  const [removeVideoFromWatchlist] = useMutation(GRAPHQL_REMOVE_WATCHLIST_MUTATION);
  const [addVideoToPlaylist] = useMutation(GRAPHQL_ADD_TO_PLAYLIST_MUTATION);
  const [removeVideoFromPlaylist] = useMutation(GRAPHQL_REMOVE_FROM_PLAYLIST_MUTATION);

  return (
    <label className="px-24 py-8 position-relative d-flex ai-center cursor-pointer playlist-checkbox">
      <input type="checkbox" value={id} defaultChecked={isChecked}
        onChange={(el) => type === 'watchlist' ? handleCheckboxWatchlist(el.target) : handleCheckboxPlaylist(el.target) }/>
      <span className="checkmark d-block"></span>
      <span className="flex-1 pl-6 playlist-checkbox__name">{name}</span>
    </label>
  );

  function handleCheckboxWatchlist(el: HTMLInputElement): void {
    if (el.checked) {
      handlePushEvent('add to watchlist', pushEventTrack?.['eventDetails.label']);
      addVideoToWatchlist(getAddVideoToWatchlistOptions(videoId, true))
        .then(() => props.showToast('toast.add.watchlist'))
        .catch(() => props.showToast('toast.add.watchlist.failed'));
    } else {
      handlePushEvent('uncheck from watchlist');
      removeVideoFromWatchlist(getRemoveVideoFromWatchlistOptions(videoId, false))
        .then(() => props.showToast('toast.remove.watchlist'))
        .catch(() => props.showToast('toast.remove.watchlist.failed'));
    }
  }

  function handleCheckboxPlaylist(el: HTMLInputElement): void {
    if (el.checked) {
      handlePushEvent('add to existing playlist', name);
      addVideoToPlaylist(getAddVideoToPlaylistOptions(videoId, id))
        .then(() => props.showToast('toast.add.playlist.video', { playlistName: name }))
        .catch(() => props.showToast('toast.add.playlist.video.failed', { playlistName: name }));
    } else {
      handlePushEvent('uncheck from playlist', name);
      removeVideoFromPlaylist(getRemoveVideoFromPlaylistOptions(videoId, id))
        .then(() => props.showToast('toast.remove.playlist.video', { playlistName: name }))
        .catch(() => props.showToast('toast.remove.playlist.video.failed', { playlistName: name }));
    }
  }

  function handlePushEvent(action: string, label?: string): void {
    if (pushEventTrack) {
      pushEventTracking(
        pushEventTrack['eventDetails.category'],
        action,
        label,
        pushEventTrack.custom
      );
    }
  }
};

PlaylistCheckboxComponent.defaultProps = {
  isChecked: false
};

export const PlaylistCheckbox = withStyles(require('./PlaylistCheckbox.scss'))(PlaylistCheckboxComponent) as React.FunctionComponent<MyProps>;
